import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight, Zap, Crown } from 'lucide-react';
import { PLANS } from '../lib/plans';

/**
 * UpgradeModal — premium modal shown when a free user hits a limit
 * (1º negócio adicional, 4ª Spark do mês, etc).
 *
 * Single source of truth: bullets/price/CTA derived from /lib/plans.js.
 */
const REASONS = {
  business_limit: {
    icon: Zap,
    title: 'Você atingiu o limite do plano gratuito',
    subtitle: 'O plano Empreendedor Gratuito permite apenas 1 negócio ativo.',
    target: 'entrepreneur_pro',
  },
  spark_limit: {
    icon: Sparkles,
    title: 'Você atingiu o limite mensal de Sparks',
    subtitle: 'O plano Investidor Gratuito permite até 3 Sparks por mês.',
    target: 'investor_pro',
  },
  generic: {
    icon: Crown,
    title: 'Desbloqueie o GetSpark Premium',
    subtitle: 'Funcionalidade exclusiva para assinantes.',
    target: null,
  },
};

const UpgradeModal = ({ open, onClose, reason = 'generic' }) => {
  const navigate = useNavigate();
  const data = REASONS[reason] || REASONS.generic;
  const Icon = data.icon;
  const targetPlan = data.target ? PLANS[data.target] : null;

  // Bullets and headline come straight from the official plan catalog
  const bullets = targetPlan?.features?.slice(0, 5) || [
    'Sem limites',
    'Perfil verificado',
    'Badge PRO ou ELITE',
    'Suporte prioritário',
  ];
  const headline = targetPlan
    ? `Turbine sua jornada com o ${targetPlan.name}.`
    : 'Turbine suas oportunidades com o GetSpark Premium.';
  const priceLabel = targetPlan ? `${targetPlan.price_label} ${targetPlan.period_label}` : null;

  const handleUpgrade = () => {
    onClose?.();
    navigate(data.target ? `/app/plans?highlight=${data.target}` : '/app/plans');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="upgrade-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-[#0a0f24]/95 backdrop-blur-2xl border border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.6)] overflow-hidden"
            data-testid="upgrade-modal"
          >
            <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-[#ffa726]/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-[#4facfe]/20 blur-3xl pointer-events-none" />

            <button
              onClick={onClose}
              data-testid="upgrade-modal-close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center text-slate-400 hover:text-white transition"
              aria-label="Fechar"
            >
              <X size={14} />
            </button>

            <div className="relative p-7 sm:p-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center shadow-[0_8px_30px_rgba(79,172,254,0.4)]">
                  <Icon size={26} className="text-white" strokeWidth={2.2} />
                </div>
                <h2 className="mt-5 heading-display text-2xl font-bold text-white">{data.title}</h2>
                <p className="mt-1.5 text-sm text-slate-400">{data.subtitle}</p>
                <p className="mt-5 text-base font-bold gradient-text">{headline}</p>
                {priceLabel && (
                  <div className="mt-3 text-xs text-slate-300" data-testid="upgrade-modal-price">
                    a partir de <span className="text-white font-bold">{priceLabel}</span>
                  </div>
                )}
              </div>

              <ul className="mt-6 space-y-2.5" data-testid="upgrade-modal-bullets">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="w-5 h-5 rounded-full bg-[#4facfe]/20 text-[#4facfe] flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <button
                onClick={handleUpgrade}
                data-testid="upgrade-modal-cta"
                className="mt-7 w-full btn-primary rounded-2xl py-3.5 text-sm font-bold flex items-center justify-center gap-2 group"
              >
                Fazer Upgrade
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={onClose}
                className="mt-3 w-full text-center text-xs text-slate-500 hover:text-slate-300 transition"
                data-testid="upgrade-modal-later"
              >
                Talvez depois
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;
